import React from 'react';
import { motion } from 'framer-motion';
import { PLANET_CONFIG, longitudeToPosition } from './clockUtils';
import { formatSignName } from '../utils/styleCard';

const PlanetaryHand = ({ 
  planet, longitude, isRetrograde, stationStatus, onClick,
  innerZoneMax = 97, edgeZoneRadius = 202, outerZoneMin = 240, outerZoneMax = 342,
  isSouthernHemisphere = false,
  isEquatorialZone = false,
  onHoverChange,
  showLabel: showLabelProp = true,
  ingressInfo = null,
  isMobile = false
}) => {
  const config = PLANET_CONFIG[planet] || { symbol: '?', color: '#C5A059', handColor: '#8B6914', zone: 'outer', zonePosition: 0.5 };
  
  // Calculate hand length based on zone
  const handLength = config.zone === 'inner' 
    ? 30 + config.zonePosition * (innerZoneMax - 30)
    : config.zone === 'edge' 
      ? edgeZoneRadius 
      : outerZoneMin + config.zonePosition * (outerZoneMax - outerZoneMin);
  
  // Apply 180° offset only for Southern TEMPERATE Hemisphere
  // Equatorial zones show true planetary positions without offset
  const applyOffset = isSouthernHemisphere && !isEquatorialZone;
  const { x: endX, y: endY } = longitudeToPosition(longitude, handLength, applyOffset);
  
  // Bead sizes scaled for 1200px viewBox - increased 50% for legibility
  const beadRadius = config.isSun ? 36 : 27;
  const symbolSize = config.isSun ? 30 : 24;
  
  // Handle hover events - notify parent for overlay rendering
  const handleMouseEnter = () => {
    if (onHoverChange) onHoverChange(true);
  };
  
  const handleMouseLeave = () => {
    if (onHoverChange) onHoverChange(false);
  };
  
  return (
    <motion.g
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.5 + (config.zonePosition * 0.1) }}
      style={{ cursor: 'pointer' }}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`planet-hand-${planet}`}
    >
      <line x1={0} y1={0} x2={endX} y2={endY} stroke={config.handColor} strokeWidth={4} opacity={0.9} strokeLinecap="round" />
      
      <g transform={`translate(${endX}, ${endY})`}>
        {config.isSun && (
          <>
            <circle cx={0} cy={0} r={beadRadius + 20} fill={config.color} opacity={0.25} />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((rayAngle) => {
              const rad = rayAngle * (Math.PI / 180);
              return (
                <line key={rayAngle}
                  x1={Math.cos(rad) * (beadRadius + 4)} y1={Math.sin(rad) * (beadRadius + 4)}
                  x2={Math.cos(rad) * (beadRadius + 24)} y2={Math.sin(rad) * (beadRadius + 24)}
                  stroke={config.color} strokeWidth={4} opacity={0.6} strokeLinecap="round"
                />
              );
            })}
          </>
        )}
        
        <circle cx={0} cy={0} r={beadRadius} fill={config.color} stroke="#1a1a1a" strokeWidth={4}
          style={{ filter: `drop-shadow(0 0 8px ${config.color})` }} />
        
        <text x={0} y={1} textAnchor="middle" dominantBaseline="central" 
          fontSize={symbolSize} fill="#1a1a1a" fontFamily="serif" fontWeight="bold"
          style={{ pointerEvents: 'none', textRendering: 'optimizeLegibility' }}>
          {config.symbol}
        </text>
        
        {isRetrograde && (
          <text x={beadRadius + 8} y={-beadRadius} fontSize="20" fill="#FF6B6B" fontWeight="bold"
            style={{ filter: 'drop-shadow(0 0 3px #000)' }}>℞</text>
        )}
        
        {stationStatus && (
          <circle cx={0} cy={0} r={beadRadius + 10} fill="none" stroke="#FFD700" strokeWidth={4} strokeDasharray="6 6" opacity={0.9}>
            <animate attributeName="r" values={`${beadRadius + 10};${beadRadius + 20};${beadRadius + 10}`} dur="2s" repeatCount="indefinite" />
          </circle>
        )}
      </g>

      {/* Ingress proximity indicator - rendered in dial-center coords (outside bead group)
          Triggers when planet is within 7 days of crossing into the next sign.
          Renders a pulsing tangent arrow pointing toward the upcoming-sign cusp. */}
      {ingressInfo && ingressInfo.days_until <= 7 && (() => {
        const direction = ingressInfo.is_retrograde ? -1 : 1;
        // Look ahead 5° along the zodiac to derive the tangent direction
        const lookaheadLon = longitude + direction * 5;
        const { x: lookX, y: lookY } = longitudeToPosition(lookaheadLon, handLength, applyOffset);
        let dx = lookX - endX;
        let dy = lookY - endY;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        dx /= len;
        dy /= len;

        const arrowLen = isMobile ? 36 : 56;
        const startOffset = beadRadius + 8;
        const baseX = endX + dx * startOffset;
        const baseY = endY + dy * startOffset;
        const tipX = endX + dx * (startOffset + arrowLen);
        const tipY = endY + dy * (startOffset + arrowLen);

        // Arrow head (perpendicular vector)
        const px = -dy;
        const py = dx;
        const headSize = isMobile ? 6 : 8;
        const h1x = tipX - dx * headSize - px * headSize;
        const h1y = tipY - dy * headSize - py * headSize;
        const h2x = tipX - dx * headSize + px * headSize;
        const h2y = tipY - dy * headSize + py * headSize;

        // Color & pulse intensity by imminence
        const daysOut = ingressInfo.days_until;
        const color = daysOut <= 1 ? '#FBBF24' : daysOut <= 3 ? '#FCD34D' : '#22D3EE';
        const pulseDur = Math.max(0.9, Math.min(2.5, daysOut * 0.4));

        const labelOffset = isMobile ? 6 : 10;
        const labelX = tipX + dx * labelOffset;
        const labelY = tipY + dy * labelOffset;
        const daysLabel = Math.max(1, Math.round(daysOut));

        return (
          <g
            style={{ pointerEvents: 'none' }}
            data-testid={`planet-${planet}-ingress-indicator`}
          >
            <line
              x1={baseX} y1={baseY} x2={tipX} y2={tipY}
              stroke={color} strokeWidth={3} strokeLinecap="round" opacity={0.85}
              style={{ filter: `drop-shadow(0 0 4px ${color})` }}
            >
              <animate attributeName="opacity" values="0.45;1;0.45" dur={`${pulseDur}s`} repeatCount="indefinite" />
            </line>
            <polygon
              points={`${tipX},${tipY} ${h1x},${h1y} ${h2x},${h2y}`}
              fill={color} opacity={0.9}
              style={{ filter: `drop-shadow(0 0 4px ${color})` }}
            >
              <animate attributeName="opacity" values="0.5;1;0.5" dur={`${pulseDur}s`} repeatCount="indefinite" />
            </polygon>
            {!isMobile && (
              <text
                x={labelX} y={labelY}
                fontSize={13} fill={color} fontWeight="700"
                textAnchor="start" dominantBaseline="middle"
                style={{ filter: 'drop-shadow(0 0 3px #000)', userSelect: 'none', fontFamily: 'sans-serif' }}
              >
                → {formatSignName(ingressInfo.to_sign)} {daysLabel}d
              </text>
            )}
          </g>
        );
      })()}
    </motion.g>
  );
};

export default PlanetaryHand;
